import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import DashboardBottomNav from '../../components/ui/DashboardBottomNav';

import { usePagination } from '../../hooks/usePagination';
import { cacheManager } from '../../utils/cacheManager';
import Pagination from '../../components/ui/Pagination';

import { treatmentAnalyticsService } from '../../services/treatmentAnalyticsService';

// Import components directly for better initial load
import MetricsStrip from './components/MetricsStrip';
import FilterControls from './components/FilterControls';
import TreatmentEffectivenessChart from './components/TreatmentEffectivenessChart';
import TreatmentRankingTable from './components/TreatmentRankingTable';
import FieldHeatMap from './components/FieldHeatMap';
import CorrelationAnalysis from './components/CorrelationAnalysis';
import AITreatmentInsights from './components/AITreatmentInsights';
import CommentThread from '../compliance-dashboard/components/CommentThread';

const TreatmentAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({});
  const [bookmarks, setBookmarks] = useState([]);
  const [dataQuality, setDataQuality] = useState({
    completeness: 98.5,
    accuracy: 96.2,
    lastSync: '2024-11-16T21:08:00Z'
  });
  const [treatments, setTreatments] = useState([]);
  const [fields, setFields] = useState([]);
  const [filteredTreatments, setFilteredTreatments] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState('');

  // Cache keys
  const TREATMENTS_CACHE_KEY = 'treatment_analytics_data';
  const FIELDS_CACHE_KEY = 'field_data';

  // Fetch data with caching
  useEffect(() => {
    const loadTreatmentData = async () => {
      try {
        setIsLoadingData(true);

        // Check cache first
        const cachedTreatments = cacheManager?.get(TREATMENTS_CACHE_KEY);
        const cachedFields = cacheManager?.get(FIELDS_CACHE_KEY);

        if (cachedTreatments && cachedFields) {
          setTreatments(cachedTreatments);
          setFields(cachedFields);
          setFilteredTreatments(cachedTreatments);
          setIsLoadingData(false);
          return;
        }

        // Fetch fresh data
        const treatmentsData = await treatmentAnalyticsService?.getAllAnalytics();
        const fieldsData = [];

        // Cache the results
        cacheManager?.set(TREATMENTS_CACHE_KEY, treatmentsData || [], 15, 30);
        cacheManager?.set(FIELDS_CACHE_KEY, fieldsData || [], 15, 30);

        setTreatments(treatmentsData || []);
        setFields(fieldsData || []);
        setFilteredTreatments(treatmentsData || []);
      } catch (error) {
        console.error('Error loading treatment data:', error);
        setError('Failed to load treatment analytics data');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadTreatmentData();
  }, []);

  // Pagination for treatments
  const {
    currentData: paginatedTreatments,
    currentPage,
    totalPages,
    goToPage,
    getPageRange,
    startIndex,
    endIndex,
    totalItems,
    resetPagination
  } = usePagination(filteredTreatments, 20);

  // Memoize metrics calculations
  const metrics = useMemo(() => {
    if (!filteredTreatments?.length) return {
      totalTreatments: 0,
      avgEffectiveness: 0,
      topProduct: 'N/A',
      mostTreatedField: 'N/A'
    };

    const totalTreatments = filteredTreatments?.length;
    const avgEffectiveness = filteredTreatments?.reduce((sum, t) => sum + (t?.effectiveness || 0), 0) / totalTreatments;
    
    const productCounts = filteredTreatments?.reduce((acc, t) => {
      acc[t?.product] = (acc?.[t?.product] || 0) + 1;
      return acc;
    }, {});
    const topProduct = Object.keys(productCounts)?.reduce((a, b) => productCounts?.[a] > productCounts?.[b] ? a : b, 'N/A');

    const fieldCounts = filteredTreatments?.reduce((acc, t) => {
      acc[t?.field] = (acc?.[t?.field] || 0) + 1;
      return acc;
    }, {});
    const mostTreatedField = Object.keys(fieldCounts)?.reduce((a, b) => fieldCounts?.[a] > fieldCounts?.[b] ? a : b, 'N/A');

    return {
      totalTreatments,
      avgEffectiveness: avgEffectiveness?.toFixed(1),
      topProduct,
      mostTreatedField
    };
  }, [filteredTreatments]);

  // Filter handler with pagination reset
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    resetPagination();
    
    const filtered = treatments?.filter(treatment => {
      const matchesProduct = !newFilters?.product || treatment?.product === newFilters?.product;
      const matchesField = !newFilters?.field || treatment?.field === newFilters?.field;
      const matchesDateRange = (!newFilters?.startDate || new Date(treatment?.date) >= new Date(newFilters.startDate)) &&
                               (!newFilters?.endDate || new Date(treatment?.date) <= new Date(newFilters.endDate));
      return matchesProduct && matchesField && matchesDateRange;
    });
    
    setFilteredTreatments(filtered);
  };

  // Add bookmark save handler
  const handleBookmarkSave = (bookmark) => {
    setBookmarks([...bookmarks, bookmark]);
  };

  // Loading spinner
  const LoadingSpinner = () => (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
    </div>
  );

  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </div>
        <DashboardBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 pb-20 md:pb-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Treatment Analytics</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Analyze treatment effectiveness and outcomes</p>
        </div>

        {/* Tab Navigation - touch-optimized */}
        <div className="mb-6 border-b border-gray-200 overflow-x-auto">
          <nav className="flex min-w-max sm:min-w-0 space-x-2 sm:space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-4 px-3 sm:px-1 border-b-2 font-medium text-sm min-h-[48px] touch-manipulation whitespace-nowrap ${
                activeTab === 'overview' ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" className="w-4 h-4" />
                Overview
              </div>
            </button>
            <button
              onClick={() => setActiveTab('comments')}
              className={`pb-4 px-3 sm:px-1 border-b-2 font-medium text-sm min-h-[48px] touch-manipulation whitespace-nowrap ${
                activeTab === 'comments' ? 'border-accent text-accent' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Icon name="MessageSquare" className="w-4 h-4" />
                Comments & Insights
              </div>
            </button>
          </nav>
        </div>

        {activeTab === 'overview' && (
          <>
            {/* Metrics Strip */}
            <MetricsStrip />

            {/* AI Treatment Insights */}
            <AITreatmentInsights
              treatments={filteredTreatments}
              metrics={metrics}
            />

            {/* Filter Controls */}
            <FilterControls 
              onFiltersChange={handleFilterChange}
              onBookmarkSave={handleBookmarkSave}
            />

            {/* Main Analytics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
              {/* Treatment Effectiveness Chart */}
              <div className="lg:col-span-2">
                <TreatmentEffectivenessChart />
              </div>

              {/* Field Heat Map */}
              <div className="lg:col-span-2">
                <FieldHeatMap />
              </div>

              {/* Correlation Analysis */}
              <div className="lg:col-span-2">
                <CorrelationAnalysis />
              </div>
            </div>

            {/* Treatment Ranking Table */}
            <TreatmentRankingTable filters={filters} />

            {/* Data Quality Indicator */}
            <div className="mt-6 bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Icon name="Database" size={20} className="text-green-600" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Data Quality</div>
                    <div className="text-xs text-gray-500">
                      Last synced: {new Date(dataQuality?.lastSync)?.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{dataQuality?.completeness}%</div>
                    <div className="text-xs text-gray-500">Complete</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-green-600">{dataQuality?.accuracy}%</div>
                    <div className="text-xs text-gray-500">Accurate</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'comments' && (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Info" className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">Treatment Insights & Collaboration</h3>
                  <p className="text-sm text-gray-600">
                    Share field observations, treatment insights, and tag colleagues with @mentions. 
                    All comments are timestamped for seamless field-to-office communication.
                  </p>
                </div>
              </div>
            </div>
            <CommentThread recordType="treatment_record" recordId="demo-treatment-id" />
          </div>
        )}
      </div>
      <DashboardBottomNav />
    </div>
  );
};

export default TreatmentAnalytics;